import React from "react";

interface SkeletonProps {
    className?: string;
    variant?: 'text' | 'circular' | 'rectangular';
    width?: string;
    height?: string;
}

const Skeleton: React.FC<SkeletonProps> = ({
    className = '',
    variant = 'text',
    width,
    height
}) => {
    const baseClass = "animate-pulse bg-coffee-200";

    const variantClass = {
        text: "rounded h-4",
        circular: "rounded-full",
        rectangular: "rounded-lg",
    }[variant];

    const style: React.CSSProperties = {};
    if (width) style.width = width;
    if (height) style.height = height;

    return (
        <div
            className={`${baseClass} ${variantClass} ${className}`}
            style={style}
        />
    );
};

// Pre-built skeleton layouts
export const CardSkeleton: React.FC = () => (
    <div className="bg-white rounded-2xl shadow-coffee-lg p-6 space-y-4">
        <div className="flex items-center gap-4">
            <Skeleton variant="circular" width="48px" height="48px" />
            <div className="flex-1 space-y-2">
                <Skeleton width="60%" height="20px" />
                <Skeleton width="40%" height="16px" />
            </div>
        </div>
        <Skeleton variant="rectangular" height="100px" />
        <div className="space-y-2">
            <Skeleton width="80%" />
            <Skeleton width="60%" />
        </div>
    </div>
);

export const ProfileSkeleton: React.FC = () => (
    <div className="max-w-4xl mx-auto px-4 py-12 space-y-8">
        <div className="text-center space-y-4">
            <Skeleton variant="circular" width="96px" height="96px" className="mx-auto" />
            <Skeleton width="200px" height="32px" className="mx-auto" />
            <Skeleton width="300px" height="20px" className="mx-auto" />
        </div>
        <div className="bg-white rounded-2xl shadow-coffee-lg p-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[1,2,3,4,5,6].map(i => (
                    <div key={i} className="space-y-2">
                        <Skeleton width="80px" height="14px" />
                        <Skeleton variant="rectangular" height="48px" />
                    </div>
                ))}
            </div>
        </div>
    </div>
);

export const ListSkeleton: React.FC<{ count?: number }> = ({ count = 3 }) => (
    <div className="space-y-4">
        {Array.from({ length: count }).map((_, i) => (
            <div key={i} className="bg-coffee-50 rounded-lg p-6 border border-coffee-200 space-y-3">
                <div className="flex justify-between items-start">
                    <div className="space-y-2 flex-1">
                        <Skeleton width="50%" height="20px" />
                        <Skeleton width="30%" height="16px" />
                    </div>
                    <Skeleton variant="rectangular" width="80px" height="28px" />
                </div>
                <Skeleton width="70%" />
                <Skeleton width="40%" />
            </div>
        ))}
    </div>
);

export default Skeleton;
